import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { accentFor } from '@/lib/accent'
import { ClientSwitcher } from './ClientSwitcher'
import type { Client } from '@/types'

/**
 * Isolation is visible on every scoped screen. A 4px accent bar and a dot,
 * not a full-width colour band — at 40px on every page the band is louder
 * than the cue needs to be (docs/02-app-shell.md §1).
 *
 * The accent colour is derived from the client id (`accentFor`) and goes in
 * inline `style`, never a class. It is always one of the fixed 8-colour
 * palette, so this can never render arbitrary CSS (docs/01-design-system.md §5).
 */
export function ClientBanner({ client }: { client: Client }) {
  const [switching, setSwitching] = useState(false)
  const accent = accentFor(client.id)

  return (
    <>
      <div className="flex h-10 items-center gap-2.5 border-b border-rule bg-surface pr-4">
        <span className="h-full w-1 shrink-0" style={{ backgroundColor: accent }} aria-hidden />
        <span
          className="ml-2 h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: accent }}
          aria-hidden
        />
        <span className="font-head text-[13px] font-semibold">{client.name}</span>
        {client.primaryDomain && (
          <>
            <span className="text-ink-faint" aria-hidden>
              ·
            </span>
            <a
              href={`https://${client.primaryDomain}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-mono text-[12px] text-ink-muted hover:text-brand-blue"
            >
              {client.primaryDomain}
              <ExternalLink size={11} aria-hidden />
            </a>
          </>
        )}

        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setSwitching(true)}>
          Switch client
        </Button>
      </div>

      <ClientSwitcher open={switching} onClose={() => setSwitching(false)} />
    </>
  )
}
